import React from 'react';

import {PartJSON} from 'biojs/Part';
import {SchemaTag} from 'folder-items/FileConstants';
import EntryLinkInput from 'metadata/EntryLinkInput';
import LongTextValueEditor from 'metadata/LongTextValueEditor';
import SelectorOptionInput from 'metadata/SelectorOptionInput';
import TagValueViewer from 'metadata/TagValueViewer';
import TextAndUrlValueEditor from 'metadata/TextAndUrlValueEditor';
import StorableLinkInput from 'samples/StorableLinkInput';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import * as SchemaFieldUtil from 'schemas/fields/SchemaFieldUtil';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import BlobLinkInput from 'schemas/tags/BlobLinkInput';
import BooleanValueEditor from 'schemas/tags/BooleanValueEditor';
import DatetimeValueEditor from 'schemas/tags/DatetimeValueEditor';
import DateValueEditor from 'schemas/tags/DateValueEditor';
import FloatValueEditor from 'schemas/tags/FloatValueEditor';
import IntegerValueEditor from 'schemas/tags/IntegerValueEditor';
import {assertNoLegalTextSelector} from 'schemas/tags/LegalTextSelectors';
import RequiredFieldPathAwareBioentityLinkInput from 'schemas/tags/RequiredFieldPathAwareBioentityLinkInput';
import * as TagUtil from 'schemas/tags/TagUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {OptionData} from 'ui-library/menus/MenuConstants';
import UnitAwareTagValueEditor from 'units/UnitAwareTagValueEditor';
import * as logging from 'util/Logging';
import {ElementKeyboardEvent} from 'util/Types';

export interface TagValueEditorProps {
  autoFocus?: boolean;
  fileId?: string;
  itemLinkAllowed?: boolean;
  noResultsOptions?: Array<{option: OptionData; onClick: () => void}> | null;
  // onChange is called with a partial tag containing only the changed fields. The caller should then merge
  // this into the old tag to get the updated tag.
  onChange: (value: object) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  schemaFieldSelectorId?: string | null;
  sequenceParts?: Array<PartJSON> | null;
  summarize?: boolean;
  tag: SchemaTag;
  isDisabled?: boolean;
}

/**
 * Component for entering a field editor based on a particular field type.
 *
 * Renders a read-only TagValueViewer for computed tags
 */
export default function TagValueEditor({
  autoFocus = false,
  fileId,
  itemLinkAllowed = true,
  noResultsOptions,
  onChange,
  onKeyDown,
  schemaFieldSelectorId,
  sequenceParts,
  summarize = false,
  tag,
  isDisabled,
}: TagValueEditorProps): JSX.Element {
  const placeholder = TagUtil.getTagPlaceholder(tag);

  if (tag && tag.tagSchemaField) {
    if (SchemaFieldUtil.isUneditableField(tag.tagSchemaField)) {
      return <TagValueViewer tag={tag} />;
    }
    assertNoLegalTextSelector(tag.tagSchemaField);
    const fieldType = TagUtil.getFullFieldType(tag.tagSchemaField.fieldType);
    switch (fieldType) {
      case FIELD_TYPES.TEXT:
        break;
      case FIELD_TYPES.LONG_TEXT:
        return (
          <LongTextValueEditor
            defaultValue={tag}
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Value'}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.INTEGER: {
        const editor = (
          <IntegerValueEditor
            defaultValue={tag}
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
        if (tag.tagSchemaField.unitSymbol != null) {
          return (
            <UnitAwareTagValueEditor editorComponent={editor} unitSymbol={tag.tagSchemaField.unitSymbol} />
          );
        }
        return editor;
      }
      case FIELD_TYPES.FLOAT: {
        const editor = (
          <FloatValueEditor
            defaultValue={tag}
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
        if (tag.tagSchemaField.unitSymbol != null) {
          return (
            <UnitAwareTagValueEditor editorComponent={editor} unitSymbol={tag.tagSchemaField.unitSymbol} />
          );
        }
        return editor;
      }
      case FIELD_TYPES.BOOLEAN:
        return (
          <BooleanValueEditor
            defaultValue={tag.booleanValue}
            onUpdate={onChange}
            placeholder={placeholder ?? 'Value'}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.DATE:
        return (
          <DateValueEditor
            defaultValue={tag}
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.DATETIME:
        return (
          <DatetimeValueEditor
            defaultValue={tag}
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.SELECTOR:
        return (
          <SelectorOptionInput
            defaultValue={{
              ...tag,
              schemaFieldSelectorOptionIds: tag.schemaFieldSelectorOptionIds ?? [],
            }}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            schemaFieldSelectorId={schemaFieldSelectorId || tag.tagSchemaField.schemaFieldSelectorId}
            shouldAllowMultiple={false}
            disabled={isDisabled}
          />
        );
      case FIELD_TYPES.BIOENTITY_LINK:
        return (
          <RequiredFieldPathAwareBioentityLinkInput
            fileId={fileId}
            autoFocus={autoFocus}
            placeholder={placeholder ?? 'Entity Link'}
            onChange={onChange}
            onKeyDown={onKeyDown}
            tag={tag}
            summarize={summarize}
            noResultsOptions={noResultsOptions}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.PART_LINK:
        return (
          <BioentityLinkInput
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Part Link'}
            sequenceParts={sequenceParts}
            tag={tag}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.TRANSLATION_LINK:
        return (
          <BioentityLinkInput
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Translation Link'}
            tag={tag}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.TRANSCRIPTION_LINK:
        return (
          <BioentityLinkInput
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Transcription Link'}
            tag={tag}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.AA_PART_LINK:
        return (
          <BioentityLinkInput
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'AA Part Link'}
            tag={tag}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.BASE_MOLECULE_LINK:
        return (
          <BioentityLinkInput
            autoFocus={autoFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Base Molecule Link'}
            tag={tag}
            chipSize={ChipSize.SMALL}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.STORABLE_LINK:
        return (
          <StorableLinkInput
            autoFocus={autoFocus}
            defaultValue={tag}
            onChange={onChange}
            onKeyDown={onKeyDown}
            summarize={summarize}
            noResultsOptions={noResultsOptions}
            placeholder={placeholder ?? 'Inventory Link'}
            includeArchived={false}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.ENTRY_LINK:
        return (
          <EntryLinkInput
            autoFocus={autoFocus}
            defaultValue={tag}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
      case FIELD_TYPES.BLOB_LINK:
        return (
          <BlobLinkInput
            defaultValue={tag}
            onChange={onChange}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        );
      default:
        logging.warn(`Unexpected fieldType: ${fieldType.type}`, {
          extra: {
            defaultValue: tag,
            fileId,
          },
        });
    }
  }
  // Use default editor for text schema tags and custom tags.
  return (
    <TextAndUrlValueEditor
      autoFocus={autoFocus}
      defaultValue={tag}
      fileId={fileId}
      itemLinkAllowed={itemLinkAllowed}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      isDisabled={isDisabled}
    />
  );
}
